import { useBaseNavLinksFromEnv } from '../header.utilities';
import { useAuth } from '@dx-ui/framework-auth-provider';
import type { UserLinks } from '../types';
import { useRouter } from 'next/router';
import type { HeaderFeatureFlags } from '../context/header-context';

type UseActiveUserLinksOptions = {
  featureFlags?: Pick<
    HeaderFeatureFlags,
    | 'shouldSuppressB2BLink'
    | 'shouldSuppressGoHiltonLink'
    | 'shouldSuppressHGVMaxLink'
    | 'shouldSuppressFriendsAndFamilyLink'
  >;
};

export const useActiveUserLinks = (options?: UseActiveUserLinksOptions): UserLinks => {
  const { guestInfo, isAuthenticated } = useAuth();
  const { asPath } = useRouter();
  const navLinks = useBaseNavLinksFromEnv({ isAuthenticated, guestInfo });
  const isB2BMember = guestInfo?.hhonors?.isB2BMember;
  const isHGVMax = Boolean(guestInfo?.hhonors?.isOwnerHGVNew) || asPath.includes('hgv-max');

  const isTMTPTeamMember =
    guestInfo?.hhonors?.isOwner ||
    guestInfo?.hhonors?.isTeamMember ||
    guestInfo?.hhonors?.isOwnerHGV;

  const isTMTPNonTeamMember =
    guestInfo?.hhonors?.isTeamMemberSpouse ||
    guestInfo?.hhonors?.isFamilyAndFriends ||
    guestInfo?.hhonors?.isLongTenure10 ||
    guestInfo?.hhonors?.isLongTenure20;

  const featureFlags = options?.featureFlags;

  // Determine which links should be shown based on user qualifications and feature flags
  // Links are shown when user qualifies AND the link is not explicitly suppressed
  const isSuppressed = {
    b2b: featureFlags?.shouldSuppressB2BLink ?? false,
    friendsAndFamily: featureFlags?.shouldSuppressFriendsAndFamilyLink ?? false,
    goHilton: featureFlags?.shouldSuppressGoHiltonLink ?? false,
    hgvMax: featureFlags?.shouldSuppressHGVMaxLink ?? false,
  };

  const shouldShow = {
    b2b: isB2BMember && !isSuppressed.b2b,
    friendsAndFamily: isTMTPTeamMember && !isSuppressed.friendsAndFamily,
    goHilton: (isTMTPNonTeamMember || isTMTPTeamMember) && !isSuppressed.goHilton,
    hgvMax: isHGVMax && !isSuppressed.hgvMax,
  };

  return {
    ...navLinks,
    b2bMemberLink: shouldShow.b2b ? navLinks?.b2bMemberLink : undefined,
    friendsAndFamilyLink: shouldShow.friendsAndFamily ? navLinks?.friendsAndFamilyLink : undefined,
    goHiltonLink: shouldShow.goHilton ? navLinks?.goHiltonLink : undefined,
    hgvMaxLink: shouldShow.hgvMax ? navLinks?.hgvMaxLink : undefined,
  };
};
